import { Action, Selector, State, StateContext } from '@ngxs/store';
import { Injectable } from '@angular/core';
import { GlobalMessageClientService } from '../../services/client/global-message-client-service/global-message-client.service';
import { CompoApp } from '../../services/CompoApp';
import { finalize, from, Observable, of, throwError } from 'rxjs';
import { UserUpdateView } from '../../models/user/input/UserUpdateView';
import { catchError, map } from 'rxjs/operators';
import { MessageDesignBUtils } from '../../content/components/message-design-b/message-design-b.utils';
import ErrorCode from '../../enums/ErrorCode';
import { UserPasswordAction } from './user-password.action';

interface UserPasswordStateModel {
  id: string;
  password: string;
  inProgress: boolean;
  resetFailed: boolean;
  resetFailedReason: number;
  wasSavedSuccessfully: boolean;
}

const getDefaults = () : UserPasswordStateModel => ({
  id: null,
  password: null,
  inProgress: false,
  resetFailed: false,
  resetFailedReason: null,
  wasSavedSuccessfully: false
});

@State<UserPasswordStateModel>({
  name: 'userPassword',
  defaults: getDefaults()
})
@Injectable()
export class UserPasswordXsState {

  constructor(
    private app: CompoApp,
    private messageClientService: GlobalMessageClientService
  ) {
  }

  @Selector()
  static inProgress(state: UserPasswordStateModel) : boolean {
    return state.inProgress;
  }

  @Selector()
  static resetFailed(state: UserPasswordStateModel) : boolean {
    return state.resetFailed;
  }

  @Selector()
  static resetFailedReason(state: UserPasswordStateModel) : number {
    return state.resetFailedReason;
  }

  @Selector()
  static wasSavedSuccessfully(state: UserPasswordStateModel) : boolean {
    return state.wasSavedSuccessfully;
  }

  @Action(UserPasswordAction.LoadPasswordData)
  public loadUser(ctx: StateContext<UserPasswordStateModel>, action: UserPasswordAction.LoadPasswordData): Observable<void> {
    const state = ctx.getState();

    if (state.inProgress) {
      return throwError(() => new Error('User request is in progress!'));
    }
    if (!action.userId) {
      return throwError(() => new Error('User id must be set!'));
    }
    if (state.id === action.userId) {
      return of(null);
    }

    ctx.patchState({
      id: action.userId,
      inProgress: false,
      resetFailed: false,
      resetFailedReason: null
    });
  }

 @Action(UserPasswordAction.ResetFailedState)
 public resetFailedState(
   ctx: StateContext<UserPasswordStateModel>,
   action: UserPasswordAction.ResetFailedState
 ) : void {
   ctx.patchState({
     resetFailed: false,
     resetFailedReason: null
   });
 }

  @Action(UserPasswordAction.UpdatePassword)
  public updatePassword(
    ctx: StateContext<UserPasswordStateModel>,
    action: UserPasswordAction.UpdatePassword
  ): Observable<void> {
    const state = ctx.getState();

    if (state.inProgress) {
      return throwError(() => new Error('User request is in progress!'));
    }
    if (!action.password) {
      return throwError(() => new Error('Password data must be set!'));
    }

    ctx.patchState({
      inProgress: true
    });

    const updateView = UserUpdateView.fromPassword(state.id, action.password);

    return from(this.app.userService.saveEditedUser(updateView))
      .pipe(
        map((user) => {
          ctx.patchState({
            id: user?.id,
            resetFailed: false,
            resetFailedReason: null,
            wasSavedSuccessfully: true
          });
          this.messageClientService.messages$.next(MessageDesignBUtils.createResetPasswordSuccess());
          ctx.patchState({
            wasSavedSuccessfully: false
          });
        }),
        catchError(error => {
          if (error.error && error.error.errorCode === ErrorCode.CAN_NOT_RESET_PASSWORD_TO_EXISTING_ONE) {
            ctx.patchState({
              resetFailed: true,
              resetFailedReason: ErrorCode.CAN_NOT_RESET_PASSWORD_TO_EXISTING_ONE
            });
            return of(void 0);
          } else if (error.error && error.error.code === ErrorCode.USER_INACTIVE_OR_NO_ACTIVE_MAPPINGS) {
            ctx.patchState({
              resetFailed: true,
              resetFailedReason: ErrorCode.USER_INACTIVE_OR_NO_ACTIVE_MAPPINGS
            });
            return of(void 0);
          } else if (error.error && error.error.code === ErrorCode.WRONG_USERNAME_OR_PASSWORD) {
            ctx.patchState({
              resetFailed: true,
              resetFailedReason: ErrorCode.WRONG_USERNAME_OR_PASSWORD
            });
            return of(void 0);
          } else {
            return throwError(() => console.error(error));
          }
        }),
        finalize(() => {
          ctx.patchState({inProgress: false});
        })
      );
  }


}
